'use client';

import { IconButton } from '@magicschool/spellbook/components/IconButton';
import { Text } from '@magicschool/spellbook/components/Text';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

export type InputChipProps = React.ComponentProps<'div'> &
  VariantProps<typeof inputChipVariants> & {
    /** Method to call when the chip is dismissed */
    onDismiss?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    /** Configuration for component default text content, labels, placeholders, and screen reader announcements. */
    text?: {
      /** The accessible label for the dismiss button */
      dismissibleLabel?: string;
    };
    /** Whether the chip is disabled */
    disabled?: boolean;
    /** Whether the chip is in a loading state */
    isLoading?: boolean;
    /** Whether the chip is read-only */
    readOnly?: boolean;
  };

export const inputChipVariants = cva(
  [
    'inline-flex',
    'items-center',
    'w-fit',
    'whitespace-nowrap',
    'shrink-0',
    'text-ellipsis',
    'transition-background',
    'transition-colors',
    'outline-none',
    'bg-default-muted-background',
    'rounded-full',
    'ps-1',
    'h-6',
    'max-h-6',
    'gap-0',
  ],
  {
    variants: {
      disabled: {
        true: 'bg-disabled-background text-disabled-foreground cursor-not-allowed pe-1',
      },
      readOnly: {
        true: 'pe-1',
      },
    },
  },
);

/**
 * A dismissible chip for displaying user-entered values in tag-style inputs.
 *
 * ### When to use
 * - Free-form tag entry (user types custom values that become chips)
 * - Email recipient fields where users type addresses
 * - Keyword/tag inputs where values aren't from a predefined list
 * - Building custom chip-based inputs not covered by MultiSelect
 *
 * ### When NOT to use
 * - Selecting from predefined options: use `MultiSelect` instead (has built-in chips)
 * - Single status indicator: use `Badge` instead (not dismissible)
 * - Filter tags from predefined set: use `MultiSelect` instead
 *
 * ### InputChip vs MultiSelect chips vs Badge
 * | Component | Dismissible | Source | Use case |
 * |-----------|-------------|--------|----------|
 * | **InputChip** | Yes | Free-form user input | Custom tag entry, email fields |
 * | **MultiSelect** | Built-in | Predefined options | Selection from dropdown |
 * | **Badge** | No | Status/category | Labels, counts, status indicators |
 *
 * ### API
 * - `onDismiss`: Callback when the X button is clicked. You manage removal from your state.
 * - `disabled`: Shows chip without dismiss button, muted styling
 * - `readOnly`: Shows chip without dismiss button, normal styling
 * - `isLoading`: Shows spinner on dismiss button (for async removal)
 * - `text.dismissibleLabel`: Accessible label for the dismiss button
 *
 * ### Building a tag input
 * InputChip is a display component — you provide the input and state management:
 * 1. Render a text input for user entry
 * 2. On Enter/comma, add the value to your array and clear input
 * 3. Render InputChip for each value in your array
 * 4. On `onDismiss`, remove that value from your array
 *
 * ### Accessibility
 * - Each chip's dismiss button has an accessible label via `text.dismissibleLabel`
 * - Configure `dismissibleLabel` to include the chip value (e.g., "Remove {label}")
 * - Chips are keyboard navigable; dismiss button is focusable
 */
export const InputChip: React.FC<InputChipProps> = ({
  children,
  className,
  text = {},
  onDismiss,
  disabled = false,
  isLoading = false,
  readOnly = false,
  ...props
}) => {
  const { dismissibleLabel = 'Dismiss' } = text;
  return (
    <div className={cn(inputChipVariants({ disabled, readOnly }), className)} {...props} data-spellbook-name="input-chip">
      <Text variant="label-3" element="span" color="inherit" className="px-1">
        {children}
      </Text>
      {!disabled && !readOnly && (
        <IconButton
          name="x"
          color="neutral"
          variant="ghost"
          size="xs"
          label={dismissibleLabel}
          data-spellbook-name="dismiss-button"
          onClick={onDismiss}
          isLoading={isLoading}
          shape="circle"
        />
      )}
    </div>
  );
};
